import React, { useState, useEffect, useRef } from 'react';
import { Eye, EyeOff, Mail, Lock, User, Check, Phone } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { toast } from 'sonner';
import { OTPVerificationForm } from './OTPVerificationForm';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/lib/api-client';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

interface AuthFormProps {
  initialMode?: 'login' | 'signup';
  onSuccess?: (user?: any) => void;
}

type AuthMode = 'login' | 'signup' | 'otp';

export function AuthForm({ initialMode = 'login', onSuccess }: AuthFormProps) {
  const navigate = useNavigate();
  const { signIn, signUp, loading, verifyOTP: authVerifyOTP } = useAuth();
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    role: 'job_seeker' as 'job_seeker' | 'employer',
    companyName: ''
  });

  const [captchaError, setCaptchaError] = useState<string | null>(null);
  const captchaRef = useRef<HTMLDivElement | null>(null);
  const [captchaWidgetId, setCaptchaWidgetId] = useState<number | null>(null);
  const [captchaSiteKey, setCaptchaSiteKey] = useState<string>('');
  const [captchaVersion, setCaptchaVersion] = useState<'v2' | 'v3'>('v2');

  // Load remembered email on component mount
  useEffect(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      setFormData(prev => ({ ...prev, email: rememberedEmail }));
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const section = await apiClient.getSectionSettings('site_info');
        const config = (section && (section as any).config) ? (section as any).config : section;
        if (mounted && config && typeof config === 'object') {
          const k = (config as any).recaptchaSiteKey || (config as any).recaptcha_site_key || '';
          if (k && typeof k === 'string') setCaptchaSiteKey(k);
          const v = (config as any).recaptchaVersion || (config as any).recaptcha_version;
          if (v === 'v3') setCaptchaVersion('v3');
        }
      } catch { }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const envKey = (import.meta as any).env?.VITE_RECAPTCHA_SITE_KEY || '';
    if (envKey) setCaptchaSiteKey(envKey);
    const envVer = (import.meta as any).env?.VITE_RECAPTCHA_VERSION || '';
    if (envVer === 'v3') setCaptchaVersion('v3');
  }, []);

  useEffect(() => {
    const loadCaptcha = () => {
      const grecaptcha = (window as any).grecaptcha;
      if (!grecaptcha) return;
      if (!captchaRef.current || !captchaSiteKey) return;
      if (captchaWidgetId !== null) return;
      try {
        grecaptcha.ready(() => {
          if (!captchaRef.current) return;
          const id = grecaptcha.render(captchaRef.current, { sitekey: captchaSiteKey });
          setCaptchaWidgetId(id);
        });
      } catch { }
    };

    if (mode === 'login') {
      if (!(window as any).grecaptcha) {
        const script = document.createElement('script');
        script.src = captchaVersion === 'v3'
          ? `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(captchaSiteKey || '')}`
          : 'https://www.google.com/recaptcha/api.js';
        script.async = true;
        script.defer = true;
        script.onload = loadCaptcha;
        document.body.appendChild(script);
      } else {
        loadCaptcha();
      }
    }
  }, [captchaSiteKey, captchaWidgetId, captchaVersion, mode]);

  // Reset form when mode changes
  useEffect(() => {
    setError('');
    if (mode !== 'otp') {
      setFormData(prev => ({
        email: rememberMe ? prev.email : '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        phoneNumber: '',
        role: 'job_seeker',
        companyName: ''
      }));
    }
  }, [mode, rememberMe]);

  // Update mode when initialMode changes
  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    console.log('🚀 AuthForm - handleSubmit called!');
    console.log('🔍 AuthForm - Mode:', mode);
    console.log('🔍 AuthForm - Form data:', { email: formData.email, passwordLength: formData.password?.length });

    e.preventDefault();
    setError('');

    try {
      if (mode === 'login') {
        console.log('🔄 AuthForm - Calling signIn...');
        setCaptchaError(null);
        let captchaToken = '';
        if (captchaSiteKey) {
          const grecaptcha = (window as any).grecaptcha;
          if (captchaVersion === 'v3') {
            if (!grecaptcha) {
              setCaptchaError('Captcha failed to load');
              return;
            }
            try {
              captchaToken = await grecaptcha.execute(captchaSiteKey, { action: 'login' });
            } catch {
              setCaptchaError('Captcha verification failed');
              return;
            }
          } else {
            if (captchaWidgetId !== null && grecaptcha) {
              captchaToken = grecaptcha.getResponse(captchaWidgetId);
            }
            if (!captchaToken) {
              setCaptchaError('Please complete the captcha verification');
              return;
            }
          }
        }
        const result = await signIn(formData.email, formData.password, captchaToken);
        if (result.error) {
          toast.error(result.error);
          setError(result.error);
        } else {
          // Handle Remember Me functionality
          if (rememberMe) {
            localStorage.setItem('rememberedEmail', formData.email);
          } else {
            localStorage.removeItem('rememberedEmail');
          }
          toast.success('Successfully signed in!');
          onSuccess?.(result.user);
        }
      } else if (mode === 'signup') {
        // Signup validation
        if (formData.password !== formData.confirmPassword) {
          toast.error('Passwords do not match');
          return;
        }
        if (formData.password.length < 6) {
          toast.error('Password must be at least 6 characters');
          return;
        }

        const fullNameOrCompany = formData.role === 'employer'
          ? formData.companyName.trim()
          : `${formData.firstName} ${formData.lastName}`.trim();

        const normalizeGmailLocal = (s: string) => String(s || '').toLowerCase().split('+')[0].replace(/\./g, '');
        const levenshtein = (a: string, b: string) => {
          const m = a.length; const n = b.length; const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
          for (let i = 0; i <= m; i++) dp[i][0] = i;
          for (let j = 0; j <= n; j++) dp[0][j] = j;
          for (let i = 1; i <= m; i++) {
            for (let j = 1; j <= n; j++) {
              const cost = a[i - 1] === b[j - 1] ? 0 : 1;
              dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + cost);
            }
          }
          return dp[m][n];
        };
        const buildExpectedLocalFromName = (first: string, last: string) => {
          const f = String(first || '').trim().toLowerCase().replace(/[^a-z]/g, '');
          const l = String(last || '').trim().toLowerCase().replace(/[^a-z]/g, '');
          if (f && l) return `${f}.${l}`;
          return f || l;
        };

        const emailVal = String(formData.email || '').trim().toLowerCase();
        const domain = emailVal.split('@')[1] || '';
        const gmailTypos = ['gamil.com', 'gmial.com', 'gmail.con', 'gmail.co', 'gmail.cm', 'gnail.com', 'gmail.comm', 'gmail.coom'];
        if (domain && (gmailTypos.includes(domain) || (domain.includes('gmail') && domain !== 'gmail.com'))) {
          const msg = 'Email not found. Please check and enter a proper Gmail address (e.g., example@gmail.com).';
          toast.error(msg);
          setError(msg);
          return;
        }

        if (domain === 'gmail.com' && formData.role === 'job_seeker') {
          const local = emailVal.split('@')[0] || '';
          const normalizedInput = normalizeGmailLocal(local).replace(/\d+$/, '');
          const expectedLocalDots = buildExpectedLocalFromName(formData.firstName, formData.lastName);
          const expectedCore = normalizeGmailLocal(expectedLocalDots);
          const hasNumbers = /\d/.test(local);
          const hasInvalidChars = /[^a-z0-9.+]/i.test(local);
          if (expectedCore) {
            const dist = levenshtein(normalizedInput, expectedCore);
            const startsWithExpected = normalizedInput.startsWith(expectedCore);
            if (dist <= 2 || (hasNumbers && startsWithExpected)) {
              const suggestionEmail = `${expectedLocalDots}@gmail.com`;
              const msg = `Email not found. Please check and enter your Gmail address. Did you mean ${suggestionEmail}?`;
              toast.error(msg);
              setError(msg);
              return;
            } else if (hasInvalidChars) {
              const msg = `Email not found. Please use a proper Gmail format (e.g., ${expectedLocalDots}@gmail.com).`;
              toast.error(msg);
              setError(msg);
              return;
            }
          }
        }

        const result = await signUp({
          email: formData.email,
          password: formData.password,
          full_name: fullNameOrCompany,
          user_type: formData.role,
          phone_number: formData.phoneNumber,
          company_data: formData.role === 'employer' ? {
            name: formData.companyName.trim(),
            description: '',
            location: '',
            industry: '',
            size: ''
          } : undefined
        });

        if (result.error) {
          toast.error(result.error);
          setError(result.error);
        } else {
          // Success - transition to OTP verification
          setPendingEmail(formData.email);
          setMode('otp');
          toast.success('SMS code successfully sent');
        }
      }
    } catch (err) {
      toast.error('An unexpected error occurred');
      setError('An unexpected error occurred');
    }
  };

  const handleOTPSuccess = async (user?: any) => {
    // After signup verification, auto-login and redirect to appropriate dashboard
    if (!user) {
      // If no user object is provided, something went wrong
      toast.error('Verification succeeded but login failed. Please try logging in.');
      setMode('login');
      setFormData(prev => ({
        ...prev,
        email: pendingEmail || prev.email,
        password: '',
        confirmPassword: ''
      }));
      return;
    }

    // Redirect based on user type
    const userType = String(user.user_type || '').toLowerCase();
    if (userType === 'super_admin') {
      navigate('/super-admin/dashboard', { replace: true });
    } else if (userType === 'admin') {
      navigate('/admin/dashboard', { replace: true });
    } else if (userType === 'employer') {
      navigate('/employer/dashboard', { replace: true });
    } else if (userType === 'job_seeker') {
      navigate('/jobseeker-dashboard', { replace: true });
    } else {
      // Fallback to home page
      navigate('/', { replace: true });
    }

    onSuccess?.(user);
  };

  const handleBackFromOTP = () => {
    setMode('signup');
    setPendingEmail('');
  };

  if (mode === 'otp') {
    return (
      <OTPVerificationForm
        tempSignupId={pendingEmail}
        email={pendingEmail}
        onSuccess={handleOTPSuccess}
        onBack={handleBackFromOTP}
      />
    );
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="text-center mb-2">
        <h2 className="text-2xl font-bold text-gray-900">
          {mode === 'login' ? 'Welcome back' : 'Create your account'}
        </h2>
        <p className="mt-1 text-xs text-gray-600">
          {mode === 'login'
            ? 'Sign in to your account to continue'
            : 'Fill in your details to get started'
          }
        </p>
      </div>



      {error && (
        <div className="mb-2 p-3 rounded border border-red-200 bg-red-50 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Role selector (signup only) placed above email */}
        {mode === 'signup' && (
          <div>
            <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
              I am a
            </label>
            <Select
              value={formData.role}
              onValueChange={(val) => setFormData(prev => ({ ...prev, role: val as any }))}
            >
              <SelectTrigger className="w-full h-10 px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all">
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="job_seeker">Job Seeker</SelectItem>
                <SelectItem value="employer">Employer</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email address
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleInputChange}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              placeholder="Enter your email"
            />
          </div>
        </div>

        {/* Signup Fields */}
        {mode === 'signup' && (
          <>
            {formData.role === 'job_seeker' ? (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      required
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="First name"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Last name"
                  />
                </div>
              </div>
            ) : (
              <div>
                <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    id="companyName"
                    name="companyName"
                    type="text"
                    required
                    value={formData.companyName}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Enter your company name"
                  />
                </div>
              </div>
            )}

            <div>
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  id="phoneNumber"
                  name="phoneNumber"
                  type="tel"
                  required
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Enter your phone number"
                />
              </div>
            </div>

            {/* Role selector moved above email */}
          </>
        )}

        {/* Password */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
            Password
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              required
              value={formData.password}
              onChange={handleInputChange}
              className="w-full pl-10 pr-12 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              placeholder="Enter your password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none transition-none p-0 h-auto leading-none bg-transparent no-hover-transform"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        {/* Confirm Password for Signup */}
        {mode === 'signup' && (
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                required
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="Confirm your password"
              />
            </div>
          </div>
        )}

        {/* Remember Me and Forgot Password (Login only) */}
        {mode === 'login' && (
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="rememberMe"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-700">
                Remember me
              </label>
            </div>
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className="text-sm text-blue-600 hover:text-blue-500 hover:underline transition-colors"
            >
              Forgot password?
            </button>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white py-2 px-4 rounded-xl transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none font-medium"
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              {mode === 'login' ? 'Signing in...' : 'Creating account...'}
            </div>
          ) : (
            mode === 'login' ? 'Sign In' : 'Create Account'
          )}
        </button>

        {mode === 'login' && captchaSiteKey && captchaVersion === 'v2' ? (
          <div className="flex justify-center">
            <div ref={captchaRef} className="mb-4" />
          </div>
        ) : (
          <></>
        )}
        {mode === 'login' && captchaError && (
          <p className="text-sm text-red-600">{captchaError}</p>
        )}
      </form>
    </div>
  );
}
